import React, { useCallback, useEffect, useState } from 'react';
import { Layout, Popconfirm, message, Button, Table, Space, Input, Select, DatePicker } from 'antd';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import MemberSidebar from './components/member.sidebar.component';
import MemberFooter from './components/member.footer.component';
import { useDispatch } from 'react-redux';
import { countTrips, readTrips, deleteTrip, duplicateTrip } from '../actions/entity.trip';
import { PageHeader } from '@ant-design/pro-components';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { green, gold, red, cyan } from '@ant-design/colors';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { readEntity } from '../actions/entity';
import { defaultPageSize, withFilterForm } from '../components/withFilterForm';

function Trips(props) {
    const { form, searchParams, setPage, setPageSize } = props;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const params = useParams();
    const { Content } = Layout;
    const [dataSource, setDataSource] = useState([]);
    const [total, setTotal] = useState(0);
    const [websiteUrl, setWebsiteUrl] = useState(null);

    useEffect(() => {
        dispatch(readEntity(params.entityId)).then((response) => {
            const url = response.result.protocol + '://' + response.result.host + (response.result.path ? `/${response.result.path}` : '') + '/zh';
            setWebsiteUrl(url);
        });
    }, [dispatch, params.entityId]);

    const duplicateTripAction = (id) => {
        dispatch(duplicateTrip(params.entityId, id)).then((response) => {
            message.success(t('trips.message.duplicate-success'));
            navigate(`/entity/${params.entityId}/trip/${response.result.id}`);
        }).catch(e => {
            console.log(e);
            message.error(t('trips.message.duplicate-failed'));
        });
    }

    const deleteTripAction = (id) => {
        dispatch(deleteTrip(params.entityId, id)).then((response) => {
            message.success(t('entities.message.delete-success'));
            setDataSource(dataSource.filter(item => item.id !== id));
        }).catch(e => {
            console.log(e);
            message.error(t('entities.message.delete-failed'));
        });
    }

    const columns = [
        {
            title: t('trips.columns.id'),
            dataIndex: 'id',
            key: 'id',
        },
        {
            title: t('trips.columns.slug'),
            dataIndex: 'slug',
            key: 'slug',
        },
        {
            title: t('trips.columns.status'),
            dataIndex: 'status',
            key: 'status',
            align: 'center',
            render: (text, record) => {
                if (typeof record.status !== 'undefined' && record.status === true) {
                    return <CheckCircleOutlined style={{ color: green[5] }} />;
                } else {
                    return <CloseCircleOutlined style={{ color: red[5] }} />;
                }
            }
        },
        {
            title: t('trips.columns.confirmed'),
            dataIndex: 'confirmed',
            key: 'confirmed',
            render: (text, record) => {
                if (typeof record.confirmed !== 'undefined' && record.confirmed === true) {
                    return t('trips.columns.confirmed.enabled');
                } else {
                    return t('trips.columns.confirmed.disabled');
                }
            }
        },
        {
            title: t('trips.columns.visibility'),
            dataIndex: 'visibility',
            key: 'visibility',
            render: (text, record) => {
                return t('trips.columns.visibility.' + record.visibility);
            }
        },
        {
            title: t('trips.columns.period'),
            dataIndex: 'period',
            key: 'period',
            render: (text, record) => {
                return moment(record.start_date).format('YYYY-MM-DD') + ' - ' + moment(record.end_date).format('YYYY-MM-DD');
            }
        },
        {
            title: t('trips.columns.created'),
            dataIndex: 'createdAt',
            key: 'createdAt'
        },
        {
            title: t('trips.columns.actions'),
            dataIndex: 'actions',
            key: 'actions',
            render: (text, record) => <Space>
                <Button size="small" type="primary" href={`/entity/${params.entityId}/trip/${record.id}`}>{t('trips.actions.edit')}</Button>
                {(typeof record.status !== 'undefined' && record.status === true) && <Button size="small" type="primary" style={{ backgroundColor: cyan[5] }} href={`${websiteUrl}/trip/${record.slug}`} target='_blank'>{t('trips.actions.viewTrip')}</Button>}
                <Button size="small" type="primary" style={{ backgroundColor: gold[5] }} href={`/entity/${params.entityId}/orders/?trip=${record.id}`}>{t('trips.actions.orders')}</Button>
                <Popconfirm
                    title={t('trips.actions.delete-confirm')}
                    onConfirm={() => deleteTripAction(record.id)}
                    okText={t('trips.actions.delete-ok')}
                    cancelText={t('trips.actions.delete-cancel')}
                >
                    <Button size="small" type="primary" danger>{t('trips.actions.delete')}</Button>
                </Popconfirm>
                <Popconfirm
                    title={t('trips.actions.duplicate-confirm')}
                    onConfirm={() => duplicateTripAction(record.id)}
                    okText={t('trips.actions.duplicate-ok')}
                    cancelText={t('trips.actions.duplicate-cancel')}
                >
                    <Button size="small" type="primary" style={{ backgroundColor: green[5] }} danger>{t('trips.actions.duplicate')}</Button>
                </Popconfirm>
            </Space>

        }
    ];

    const loadTrips = useCallback(() => {
        const filters = {};
        if (typeof searchParams.get('slug') !== 'undefined' && searchParams.get('slug') && searchParams.get('slug') !== '') {
            filters['slug'] = searchParams.get('slug');
        }
        if (typeof searchParams.get('status') !== 'undefined' && searchParams.get('status') && searchParams.get('status') !== '') {
            filters['status'] = searchParams.get('status');
        }
        if (typeof searchParams.get('confirmed') !== 'undefined' && searchParams.get('confirmed') && searchParams.get('confirmed') !== '') {
            filters['confirmed'] = searchParams.get('confirmed');
        }
        if (typeof searchParams.get('visibility') !== 'undefined' && searchParams.get('visibility') && searchParams.get('visibility') !== '') {
            filters['visibility'] = searchParams.get('visibility');
        }
        if (typeof searchParams.get('start_date_from') !== 'undefined' && searchParams.get('start_date_from') && searchParams.get('start_date_from') !== '') {
            filters['start_date_from'] = searchParams.get('start_date_from');
        }
        if (typeof searchParams.get('start_date_to') !== 'undefined' && searchParams.get('start_date_to') && searchParams.get('start_date_to') !== '') {
            filters['start_date_to'] = searchParams.get('start_date_to');
        }
        if (typeof searchParams.get('end_date_from') !== 'undefined' && searchParams.get('end_date_from') && searchParams.get('end_date_from') !== '') {
            filters['end_date_from'] = searchParams.get('end_date_from');
        }
        if (typeof searchParams.get('end_date_to') !== 'undefined' && searchParams.get('end_date_to') && searchParams.get('end_date_to') !== '') {
            filters['end_date_to'] = searchParams.get('end_date_to');
        }
        const filtersWithPagination = { ...filters, page: searchParams.get('page'), pageSize: searchParams.get('pageSize') };
        dispatch(countTrips(params.entityId, filters)).then((data) => {
            setTotal(data.result);
        }).catch(error => {
            try {
                let responseMessage = error.response.data.message;
                console.log(responseMessage);
                message.error(responseMessage);
            } catch (e) {
                console.log('Failed:', e);
                message.error(JSON.stringify(e));
            }
        });
        dispatch(readTrips(params.entityId, filtersWithPagination)).then((data) => {
            setDataSource(data.result);
        }).catch((error) => {
            try {
                setDataSource([]);
                let responseMessage = error.response.data.message;
                console.log(responseMessage);
                message.error(responseMessage);
            } catch (e) {
                console.log('Failed:', e);
                message.error(JSON.stringify(e));
            }
        });
    }, [dispatch, searchParams, params.entityId]);

    useEffect(() => {
        loadTrips();
    }, [loadTrips]);

    return <>
        <MemberSidebar />
        <Layout className="site-layout">
            <Content style={{ margin: '0 16px' }}>
                <div className="site-layout-background">
                    <PageHeader
                        className="site-page-header"
                        onBack={() => navigate(`/entity/${params.entityId}/dashboard`)}
                        title={t('trips.title')}
                        subTitle={t('trips.subtitle')}
                        extra={[
                            <Button key="create" type="primary" href={`/entity/${params.entityId}/trip`}>{t('trips.actions.new')}</Button>
                        ]}
                    />
                    {form}
                    <Table
                        size="small"
                        rowKey={record => record.id}
                        dataSource={dataSource}
                        columns={columns}
                        pagination={{
                            total: total,
                            onChange(page, pageSize) {
                                setPage(page);
                                setPageSize(pageSize);
                            },
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            defaultCurrent: searchParams.get('page') ? parseInt(searchParams.get('page')) : 1,
                            defaultPageSize: searchParams.get('pageSize') ? parseInt(searchParams.get('pageSize')) : defaultPageSize,
                            showSizeChanger: true,
                            pageSizeOptions: [defaultPageSize.toString(), '250', '500', '1000'],
                            position: ['topRight', 'bottomRight']
                        }}
                    />
                </div>
            </Content>
            <MemberFooter />
        </Layout>
    </>;
}

export default withFilterForm(Trips, [
    { name: 'slug', label: 'Slug', formItem: <Input placeholder='Slug' /> },
    { name: 'status', label: 'Status', formItem: <Select placeholder='Status' options={[{ label: 'Active', value: '1' }, { label: 'Inactive', value: '0' }]} allowClear={true} /> },
    { name: 'confirmed', label: 'Confirmed', formItem: <Select placeholder='Confirmed' options={[{ label: 'Confirmed', value: '1' }, { label: 'Unconfirmed', value: '0' }]} allowClear={true} /> },
    { name: 'visibility', label: 'Visibility', formItem: <Select placeholder='Visibility' options={[{ label: 'Public', value: 'public' }, { label: 'Private', value: 'private' }]} allowClear={true} /> },
    { type: 'date', name: 'start_date_from', label: 'Start Date From', formItem: <DatePicker placeholder='Start Date From' /> },
    { type: 'date', name: 'start_date_to', label: 'Start Date To', formItem: <DatePicker placeholder='Start Date To' /> },
    { type: 'date', name: 'end_date_from', label: 'End Date From', formItem: <DatePicker placeholder='End Date From' /> },
    { type: 'date', name: 'end_date_to', label: 'End Date To', formItem: <DatePicker placeholder='End Date To' /> },
], null, null, null, defaultPageSize, 1, ['slug', 'status', 'confirmed', 'visibility', 'start_date_from', 'start_date_to', 'end_date_from', 'end_date_to']);